import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Alert } from '../ui/alert';
import api from '../../lib/api';

const PaymentForm = ({
  subscriptionId,
  amount,
  onSuccess,
  onError,
  customerData = {},
  existingOrderId = null,
}) => {
  const { user, token } = useAuth();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [orderId, setOrderId] = useState(null);

  // Load Razorpay script on component mount
  useEffect(() => {
    const loadScript = async () => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => {
          resolve(true);
        };
        script.onerror = () => {
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    const initializeRazorpay = async () => {
      try {
        const res = await loadScript();
        if (!res) {
          setPaymentError(
            'Razorpay SDK failed to load. Please check your internet connection.'
          );
          return;
        }
        setScriptLoaded(true);
      } catch (err) {
        setPaymentError(
          'Failed to load payment gateway. Please refresh the page and try again.'
        );
      }
    };

    initializeRazorpay();

    // If an existing order ID is provided, set it immediately
    if (existingOrderId) {
      console.log('Using existing order ID:', existingOrderId);
      setOrderId(existingOrderId);
    }
  }, [existingOrderId]);

  // Reference to track if order creation has been attempted
  const orderCreationAttemptedRef = useRef(false);

  // Create an order on component mount only if no existing order ID is provided
  useEffect(() => {
    // Skip order creation if we already have an existing order ID
    if (existingOrderId) {
      return;
    }

    const createOrder = async () => {
      // Prevent duplicate order creation
      if (orderCreationAttemptedRef.current) {
        console.log(
          'Order creation already attempted, skipping duplicate request'
        );
        return;
      }

      if (!token) {
        setPaymentError('You must be logged in to make a payment');
        return;
      }

      try {
        // Mark that we've attempted to create an order
        orderCreationAttemptedRef.current = true;

        setProcessingPayment(true);
        console.log('Creating order with data:', {
          subscription_id: subscriptionId,
          amount: amount,
        });
        const response = await api.post(
          'payments/create-order',
          {
            subscription_id: subscriptionId,
            amount: amount,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log('Order created successfully:', response.data);
        // Check if the response contains the expected razorpay_order_id
        if (response.data.razorpay_order_id) {
          setOrderId(response.data.razorpay_order_id);
        } else if (response.data.id) {
          // Sometimes the API might return a different structure
          console.log(
            'Using ID as order ID since razorpay_order_id was not found'
          );
          setOrderId(response.data.id);
        } else {
          console.error('No order ID found in response:', response.data);
          setPaymentError('Failed to get order ID from server');
        }
        setProcessingPayment(false);
      } catch (err) {
        console.error('Error creating order:', err);
        setPaymentError(
          err.response?.data?.detail ||
            'Failed to initialize payment. Please try again.'
        );
        setProcessingPayment(false);
      }
    };

    if (subscriptionId && amount > 0 && !orderId) {
      createOrder();
    }

    // Cleanup function - will run when component unmounts
    return () => {
      // If component unmounts before order is created, we want to allow creation on next mount
      if (!orderId) {
        orderCreationAttemptedRef.current = false;
      }
    };
  }, [subscriptionId, amount, token, orderId, existingOrderId]);

  const handlePayNow = async () => {
    if (!scriptLoaded || processingPayment || !orderId) {
      console.log(
        'Cannot proceed with payment:',
        !scriptLoaded
          ? 'Script not loaded'
          : !orderId
          ? 'No order ID'
          : 'Payment in progress'
      );
      return;
    }

    setProcessingPayment(true);
    setPaymentError('');

    try {
      // Get the Razorpay key from environment or use a fallback
      const razorpayKey =
        process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_9Qd6Zx1r0l1LaG';
      console.log('Using Razorpay key:', razorpayKey);

      // Log the order ID to verify it's in the correct format
      console.log('Opening Razorpay checkout with order ID:', orderId);

      const options = {
        key: razorpayKey,
        amount: amount * 100, // Convert to paise
        currency: 'INR',
        name: 'Curators Homes',
        description: 'Flower Subscription',
        order_id: orderId,
        prefill: {
          name: customerData?.name || user?.name || '',
          email: customerData?.email || user?.email || '',
          contact: customerData?.phone || user?.phone || '',
        },
        notes: {
          subscription_id: subscriptionId,
        },
        theme: {
          color: '#7C4DFF',
        },
        handler: function (response) {
          handlePaymentSuccess(response);
        },
        modal: {
          ondismiss: function () {
            setProcessingPayment(false);
            setPaymentError('Payment cancelled');
            if (onError) onError('Payment cancelled by user');
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      console.error('Razorpay initialization error:', err);
      setPaymentError(
        'Failed to initialize payment gateway. Please try again.'
      );
      setProcessingPayment(false);
      if (onError) onError(err);
    }
  };

  const handlePaymentSuccess = async (response) => {
    try {
      // Verify payment with backend
      const verifyResponse = await api.post(
        'payments/verify',
        {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (verifyResponse.data.success) {
        if (onSuccess) {
          onSuccess(response);
        }
      } else {
        setPaymentError('Payment verification failed');
        if (onError) {
          onError('Payment verification failed');
        }
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setPaymentError(
        err.response?.data?.detail ||
          'Payment verification failed. Please contact support.'
      );
      if (onError) {
        onError(err);
      }
    } finally {
      setProcessingPayment(false);
    }
  };

  // Format amount for display
  const formattedAmount = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

  return (
    <div className="payment-form p-6 border rounded-lg">
      <h3 className="text-xl font-semibold mb-4">Payment Details</h3>

      <div className="mb-6">
        <p className="text-gray-700 mb-2">Amount to pay:</p>
        <p className="text-2xl font-bold">{formattedAmount}</p>
      </div>

      {paymentError && (
        <Alert variant="destructive" className="mb-4">
          {paymentError}
        </Alert>
      )}

      <div className="payment-info bg-gray-50 p-4 rounded-md mb-6">
        <h4 className="font-medium mb-2">Payment Information</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Secure payment via Razorpay</li>
          <li>
            • Multiple payment methods supported including Credit/Debit cards,
            UPI, Netbanking
          </li>
          <li>• Your payment information is securely processed</li>
        </ul>
      </div>

      <Button
        onClick={handlePayNow}
        disabled={!scriptLoaded || !orderId || processingPayment}
        className="w-full py-6 text-lg"
      >
        {processingPayment ? 'Processing...' : 'Pay Now'}
      </Button>

      <div className="mt-4 text-xs text-center text-gray-500">
        By proceeding with the payment, you agree to our Terms of Service.
      </div>
    </div>
  );
};

export default PaymentForm;
